import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Grid, Modal, MODAL_FOOTER_CLASSES, Typography } from "@sumprio/ui";
import ModalPortalRoot from "components/common/modals/ModalPortalRoot";
import { ImportIcon } from "@sumprio/ui/icons";
import type { ViewableDocument } from "./types";
import { PdfPreviewPanel } from "./PdfPreviewPanel";
import { SpreadsheetPreviewPanel } from "./SpreadsheetPreviewPanel";
import { TextFilePreviewPanel } from "./TextFilePreviewPanel";
import { DocxPreviewPanel } from "./DocxPreviewPanel";
import {
  getFileTypeIcon,
  isDocxFile,
  isImageFile,
  isPdfFile,
  isPreviewUnavailableFile,
  isSpreadsheetFile,
  isTextFile,
  resolvePreviewFileName,
} from "./utils";
import { useBlobPreviewUrl } from "./useBlobPreviewUrl";
import { useSpreadsheetPreview } from "./useSpreadsheetPreview";
import { useTextFilePreview } from "./useTextFilePreview";
import { useDocxPreview } from "./useDocxPreview";
import { getDocumentUrl } from "./documentUrl";
import "./ViewDocumentModal.scss";

export interface ViewDocumentModalProps {
  open: boolean;
  attachment: ViewableDocument | null;
  onClose: () => void;
  resolveDocumentUrl?: (_docPath: string) => Promise<string | null>;
}

export default function ViewDocumentModal({
  open,
  attachment,
  onClose,
  resolveDocumentUrl,
}: ViewDocumentModalProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Keep last attachment while Modal animates closed / click-through guard runs.
  const [displayAttachment, setDisplayAttachment] = useState<ViewableDocument | null>(attachment);
  useEffect(() => {
    if (attachment) {
      setDisplayAttachment(attachment);
      return;
    }
    if (!open) {
      const timer = window.setTimeout(() => setDisplayAttachment(null), 400);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [attachment, open]);

  const files = useMemo(() => {
    if (!displayAttachment) return [];
    if (Array.isArray(displayAttachment.files) && displayAttachment.files.length > 0) {
      return displayAttachment.files.filter((f) => Boolean(f?.docPath));
    }
    if (displayAttachment.docPath) {
      return [{ docPath: displayAttachment.docPath, docName: displayAttachment.docName }];
    }
    return [];
  }, [displayAttachment]);

  const [fileIndex, setFileIndex] = useState(0);
  const safeIndex = files.length === 0 ? 0 : Math.min(fileIndex, files.length - 1);
  const active = files[safeIndex];

  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFileIndex(0);
  }, [open, displayAttachment?.docPath, displayAttachment?.title, displayAttachment?.docName]);

  const docPath = active?.docPath ?? "";
  const fallbackUrl = docPath ? getDocumentUrl(docPath) : "";
  const url =
    resolveDocumentUrl &&
    resolvedUrl !== null &&
    resolvedUrl !== undefined &&
    String(resolvedUrl).trim()
      ? String(resolvedUrl).trim()
      : fallbackUrl;
  const fileName = active?.docName ?? docPath.split("/").pop() ?? "document";
  const documentTitle = displayAttachment?.title?.trim() || displayAttachment?.docName || fileName;
  const previewFileName = resolvePreviewFileName(fileName, docPath);
  const previewUnavailable = isPreviewUnavailableFile(previewFileName);
  const isImage = isImageFile(previewFileName);
  const isPdf = isPdfFile(previewFileName);
  const isText = isTextFile(previewFileName);
  const isSpreadsheet = isSpreadsheetFile(previewFileName);
  const isDocx = isDocxFile(previewFileName);
  const showCarousel = files.length > 1;
  const textPreview = useTextFilePreview(
    open && isText && !urlLoading && !urlError && Boolean(url),
    url
  );
  const spreadsheetPreview = useSpreadsheetPreview(
    open && isSpreadsheet && !urlLoading && !urlError && Boolean(url),
    url
  );
  const docxPreview = useDocxPreview(
    open && isDocx && !urlLoading && !urlError && Boolean(url),
    url
  );
  // Always embed via blob URL so Chrome can show its native PDF password dialog.
  const pdfPreview = useBlobPreviewUrl(
    open && isPdf && !urlLoading && !urlError && Boolean(url),
    url,
    "application/pdf"
  );

  useEffect(() => {
    if (!open || !docPath) {
      setResolvedUrl(null);
      setUrlLoading(false);
      setUrlError(false);
      return;
    }
    if (!resolveDocumentUrl) {
      setResolvedUrl(null);
      setUrlLoading(false);
      return;
    }
    let cancelled = false;
    setUrlLoading(true);
    setUrlError(false);
    resolveDocumentUrl(docPath)
      .then((u) => {
        if (cancelled) return;
        setResolvedUrl(u ?? null);
        if (u === null || u === undefined) setUrlError(true);
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedUrl(null);
        setUrlError(true);
      })
      .finally(() => {
        if (!cancelled) setUrlLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, docPath, resolveDocumentUrl, safeIndex]);

  const handleDownload = () => {
    const downloadHref = pdfPreview.blobUrl ?? url;
    if (!downloadHref) return;
    const link = document.createElement("a");
    link.href = downloadHref;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  if (!displayAttachment) return null;

  const FileTypeIcon = getFileTypeIcon(fileName);
  const modalTitle = showCarousel
    ? `${documentTitle} (${safeIndex + 1} of ${files.length})`
    : documentTitle;

  const footer = (
    <>
      <div className={MODAL_FOOTER_CLASSES.cancel}>
        <Grid display="flex" alignItems="center" gap="sm">
          <FileTypeIcon size="xl" />
          <Grid display="flex" flexDirection="column" gap="xs">
            <Typography variant="body" bodySize="sm" bodyWeight="medium">
              {fileName}
            </Typography>
            {displayAttachment.fileSize && !showCarousel && (
              <Typography variant="body" bodySize="xs" bodyWeight="regular" color="secondary">
                {displayAttachment.fileSize}
              </Typography>
            )}
            {showCarousel ? (
              <Typography variant="body" bodySize="xs" bodyWeight="regular" color="secondary">
                File {safeIndex + 1} of {files.length}
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      </div>
      <div className={MODAL_FOOTER_CLASSES.actions}>
        {showCarousel ? (
          <Grid display="flex" alignItems="center" gap="sm">
            <Button
              variant="brand-secondaryGrey"
              size="sm"
              disabled={safeIndex <= 0}
              onClick={() => setFileIndex((i) => Math.max(0, i - 1))}
            >
              Previous
            </Button>
            <Button
              variant="brand-secondaryGrey"
              size="sm"
              disabled={safeIndex >= files.length - 1}
              onClick={() => setFileIndex((i) => Math.min(files.length - 1, i + 1))}
            >
              Next
            </Button>
          </Grid>
        ) : null}
        <Button
          iconOnly
          icon={ImportIcon}
          variant="brand-secondaryGrey"
          size="sm"
          onClick={handleDownload}
          aria-label="Download"
        />
      </div>
    </>
  );

  return (
    <ModalPortalRoot open={open} variant="center">
      <Modal
        variant="center"
        open={open}
        onClose={handleClose}
        closeOnBackdropClick={false}
        closeOnEscape={false}
        title={modalTitle}
        showCloseButton
        headerActionIcon={ImportIcon}
        onHeaderAction={handleDownload}
        headerActionAriaLabel="Download"
        showHeaderDivider={false}
        showFooterDivider={false}
        showCancelButton={false}
        showSecondaryButton={false}
        showPrimaryButton={false}
        footer={footer}
        sideSize="lg"
        width="min(90vw, 900px)"
      >
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="md"
          className="view-document-modal__content"
        >
          {previewUnavailable ? (
            <Grid
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="md"
              padding="2xl"
              className="view-document-modal__fallback"
            >
              <Typography variant="body" bodySize="md" bodyWeight="regular" color="secondary">
                Preview not available.
              </Typography>
            </Grid>
          ) : urlLoading ? (
            <Grid
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              padding="2xl"
            >
              <Typography variant="body" bodySize="md" bodyWeight="regular" color="secondary">
                Loading…
              </Typography>
            </Grid>
          ) : urlError || !url ? (
            <Grid
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="md"
              padding="2xl"
              className="view-document-modal__fallback"
            >
              <Typography variant="body" bodySize="md" bodyWeight="regular" color="secondary">
                Could not load document. You can still try downloading.
              </Typography>
            </Grid>
          ) : isImage ? (
            <div className="view-document-modal__image-wrapper">
              <img src={url} alt={fileName} className="view-document-modal__image" />
            </div>
          ) : isPdf ? (
            <PdfPreviewPanel
              loading={pdfPreview.loading}
              error={pdfPreview.error}
              blobUrl={pdfPreview.blobUrl}
              fallbackUrl={url}
              title={fileName}
              classNamePrefix="view-document-modal"
            />
          ) : isText ? (
            <TextFilePreviewPanel
              loading={textPreview.loading}
              error={textPreview.error}
              content={textPreview.content}
              classNamePrefix="view-document-modal"
            />
          ) : isSpreadsheet ? (
            <SpreadsheetPreviewPanel
              loading={spreadsheetPreview.loading}
              error={spreadsheetPreview.error}
              data={spreadsheetPreview.data}
              truncated={spreadsheetPreview.truncated}
              classNamePrefix="view-document-modal"
            />
          ) : isDocx ? (
            <DocxPreviewPanel
              loading={docxPreview.loading}
              error={docxPreview.error}
              content={docxPreview.content}
              classNamePrefix="view-document-modal"
            />
          ) : (
            <Grid
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="md"
              padding="2xl"
              className="view-document-modal__fallback"
            >
              <Typography variant="body" bodySize="md" bodyWeight="regular" color="secondary">
                Preview not available for this file type.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Modal>
    </ModalPortalRoot>
  );
}
